/**
 * Module dependencies.
 */

import {
  Box,
  RawHtml,
  color,
  media,
  normalizeNewLine,
  units
} from '@untile/react-components';

import { Button, Subtitle, Type } from 'src/components/core';
import { NextPage } from 'next/types';
import { contacts } from 'src/core/content-config/contacts';
import { socialNetworks } from 'src/core/content-config/footer';
import { theme } from 'styled-tools';
import ContactForm from 'src/components/contact-us/contact-form';
import Container from 'src/components/core/layout/container';
import Metatags from 'src/components/core/metatags';
import map from 'lodash/map';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * Constants.
 */

const fax = contacts.fax.replace(/ /g, '');
const gpsCoordinates = `${contacts.gpsCoordinates.latitude}, ${contacts.gpsCoordinates.longitude}`;
const phone = contacts.phone.replace(/ /g, '');
const whatsapp = contacts.whatsapp.replace(/ /g, '');

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  background-color: ${color('backgrounds.sage02')};
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(6)}) 0
    ${units(11)};
  position: relative;
  width: 100vw;

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(
    10
  )}) 0 ${units(18)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      'title    . . subtitles'
      'contacts . . form     ';
    grid-template-columns: 4fr 1fr ${theme('grid.columnsGutter')}px 5fr;
  `}

  ${media.min('md')`
    grid-template-areas:
      '. title    . . subtitles .'
      '. contacts . . form      .';
    grid-template-columns: 1fr 3fr 2fr ${theme('grid.columnsGutter')}px 5fr 1fr;
  `}

  ${media.min('lg')`
    grid-template-columns: 1fr 4fr 2fr ${theme('grid.columnsGutter')}px 4fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;

  ${media.max('md')`
    margin-bottom: ${units(4)};
  `}
`;

/**
 * `SubtitleWrapper` styled component.
 */

const SubtitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: subtitles;
  margin-bottom: ${units(4)};
`;

/**
 * `StyledSubtitle` styled component.
 */

const StyledSubtitle = styled(Subtitle)`
  color: ${color('black')};
`;

/**
 * `ContactsWrapper` styled component.
 */

const ContactsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: contacts;

  ${media.max('md')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Label` styled component.
 */

const Label = styled(Type.Paragraph)`
  color: ${color('grey500')};

  &:last-of-type {
    margin-bottom: ${units(1)};
  }
`;

/**
 * `DirectionsButton` styled component.
 */

const DirectionsButton = styled(Button).attrs({
  colorTheme: 'tertiary',
  rel: 'noopener',
  size: 'small',
  target: '_blank'
})`
  margin-bottom: ${units(4)};
`;

/**
 * `WhatsappButton` styled component.
 */

const WhatsappButton = styled(Button)`
  margin-bottom: ${units(4)};
`;

/**
 * `Link` styled component.
 */

const Link = styled.a.attrs({
  rel: 'noopener',
  target: '_blank'
})`
  ${theme('typography.styles.p')}

  color: ${color('grey800')};
  margin: 0 0 ${units(4)};
  text-decoration: none;
  transition: opacity ${theme('animations.defaultTransition')};
  width: max-content;

  &:focus,
  &:hover {
    opacity: 0.6;
  }
`;

/**
 * `SocialNetworksWrapper` styled component.
 */

const SocialNetworksWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Link} {
    margin-bottom: ${units(0.5)};
  }
`;

/**
 * `StyledContactForm` styled component.
 */

const StyledContactForm = styled(ContactForm)`
  grid-area: form;
`;

/**
 * `Disclaimer` styled component.
 */

const Disclaimer = styled(Type.Small)`
  color: ${color('grey400')};
`;

/**
 * `ContactUs` page.
 */

const ContactUs: NextPage = () => {
  const { translate } = useTranslate();
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <>
      <Metatags
        description={translate('contact-us:subtitleOne')}
        pageTitle={translate('contact-us:title')}
        title={translate('contact-us:title')}
      />

      <Wrapper>
        <Container>
          <Grid>
            <Title {...animProps}>{translate('contact-us:title')}</Title>

            <ContactsWrapper>
              <Box
                marginBottom={units(2)}
                {...animProps}
              >
                <Label>{translate('common:labels.address')}</Label>

                <RawHtml element={Type.Paragraph}>
                  {normalizeNewLine(contacts.address)}
                </RawHtml>
              </Box>

              <div {...animProps}>
                <DirectionsButton
                  href={`https://maps.google.com?q=${gpsCoordinates}`}
                >
                  {translate('common:actions.getDirections')}
                </DirectionsButton>
              </div>

              <Box
                {...animProps}
                marginBottom={units(4)}
              >
                <Label>{translate('common:labels.gpsCoordinates')}</Label>

                <Type.Paragraph>{gpsCoordinates}</Type.Paragraph>
              </Box>

              <Box
                {...animProps}
                marginBottom={units(4)}
              >
                <Label>{translate('common:labels.email')}</Label>

                <Link href={`mailto:${contacts.email}`}>{contacts.email}</Link>
              </Box>

              <Box
                {...animProps}
                marginBottom={units(4)}
              >
                <Label>{translate('common:labels.emailHR')}</Label>

                <Link href={`mailto:${contacts.emailHR}`}>{contacts.emailHR}</Link>
              </Box>

              <Box
                {...animProps}
                marginBottom={units(4)}
              >
                <Label>{translate('common:labels.phone')}</Label>

                <Link href={`tel:${phone}`}>{`${contacts.phone} *`}</Link>
              </Box>

              <Box
                {...animProps}
                marginBottom={units(4)}
              >
                <Label>{translate('common:labels.fax')}</Label>

                <Link href={`tel:${fax}`}>{`${contacts.fax} *`}</Link>
              </Box>

              <div {...animProps}>
                <WhatsappButton
                  colorTheme={'secondary'}
                  href={`https://wa.me/${whatsapp.replace('+', '')}`}
                  rel={'noopener'}
                  target={'_blank'}
                >
                  {'Whatsapp'}
                </WhatsappButton>
              </div>

              <Box
                {...animProps}
                marginBottom={units(10)}
              >
                <Label>{translate('common:labels.socialNetworks')}</Label>

                <SocialNetworksWrapper>
                  {map(socialNetworks.options, ({ href, label }, index) => (
                    <Link
                      href={href}
                      key={index}
                    >
                      {label}
                    </Link>
                  ))}
                </SocialNetworksWrapper>
              </Box>

              <Box
                {...animProps}
                marginBottom={units(4)}
              >
                <RawHtml element={Disclaimer}>
                  {translate('contact-us:disclaimer')}
                </RawHtml>
              </Box>
            </ContactsWrapper>

            <SubtitleWrapper>
              <Type.H3 {...animProps}>
                {translate('contact-us:subtitleOne')}
              </Type.H3>

              <StyledSubtitle {...animProps}>
                {translate('contact-us:subtitleTwo')}
              </StyledSubtitle>
            </SubtitleWrapper>

            <StyledContactForm />
          </Grid>
        </Container>
      </Wrapper>
    </>
  );
};

/**
 * Export `ContactUs` page.
 */

export default ContactUs;
